import { Download } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { studioApi, useAuditHead, useAuditLog, useStudioClients, useStudioPeople } from '@/api/studio';
import type { AuditEvent, AuditFilter } from '@/api/types';
import { DownloadButton } from '@/components/DownloadButton';
import { QueryPending } from '@/components/QueryState';
import { Empty } from '@/components/ui';
import { auditLabel, clientRoleLabel, studioRoleLabel } from '@/lib/access';
import { formatWhen } from '@/lib/format';

const roleName = (r: string) => (studioRoleLabel as Record<string, string>)[r] ?? (clientRoleLabel as Record<string, string>)[r] ?? r;
const scalar = (v: unknown) => (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : null);

/** The server records codes (statuses, field names, Python booleans); people read words. */
const valueWords: Record<string, string> = {
  True: 'да',
  False: 'нет',
  true: 'да',
  false: 'нет',
  new: 'новый',
  estimated: 'оценён',
  accepted: 'принят',
  declined: 'отклонён',
  in_scope: 'в объёме',
  on_track: 'идёт по плану',
  at_risk: 'есть риск',
  waiting_client: 'ждём клиента',
  paused: 'на паузе',
  done: 'завершён',
  lead: 'потенциальный',
  active: 'в работе',
  former: 'бывший',
  fixed: 'фиксированная услуга',
  retainer: 'регулярные платежи',
};
const fieldWords: Record<string, string> = {
  acts_enabled: 'акты',
  stage: 'этап',
  manager_id: 'менеджер',
  name: 'название',
  legal: 'реквизиты',
  title: 'название',
  summary: 'описание',
  status: 'статус',
  health: 'статус',
  owner_id: 'ведёт',
  team_ids: 'команда',
  price: 'цена',
  until: 'договор до',
  billing: 'график счетов',
  finish_by: 'сдача',
  milestones: 'этапы',
  rhythm: 'регулярная работа',
};
const word = (v: string) => valueWords[v] ?? (/^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(v).toLocaleDateString('ru-RU', { timeZone: 'UTC' }) : v);

/** «до → после» in words: a scalar pair (stage, role, status) or per field («акты: да → нет»). */
function change(e: AuditEvent): string | null {
  const { before, after } = e.data as { before?: unknown; after?: unknown };
  const name = e.action === 'role_changed' ? roleName : word;
  const a = scalar(after);
  if (a !== null) {
    const b = scalar(before);
    if (b === a) return null;
    return b === null ? `→ ${name(a)}` : `${name(b)} → ${name(a)}`;
  }
  if (after && typeof after === 'object') {
    const was = (before && typeof before === 'object' ? before : {}) as Record<string, unknown>;
    const parts = Object.entries(after as Record<string, unknown>).map(([k, v]) => {
      const label = fieldWords[k] ?? k;
      const nv = scalar(v);
      const ov = scalar(was[k]);
      if (nv === null) return label;
      return ov === null ? `${label}: ${word(nv)}` : `${label}: ${word(ov)} → ${word(nv)}`;
    });
    return parts.length ? parts.join(', ') : null;
  }
  const fields = (e.data as { fields?: unknown }).fields;
  return Array.isArray(fields) && fields.length ? fields.map((f) => fieldWords[String(f)] ?? String(f)).join(', ') : null;
}

/** What the event was about, when the server kept its name (a deleted project, a document). */
const subject = (e: AuditEvent): string | null => scalar((e.data as { title?: unknown }).title);

function Rows({ items, showClient }: { items: AuditEvent[]; showClient: boolean }) {
  return (
    <ul className="divide-y divide-line border-y border-line text-sm">
      {items.map((e) => (
        <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 py-2.5">
          <span className="w-36 shrink-0 text-xs text-muted">{formatWhen(e.at)}</span>
          <span className="font-medium">{e.actor?.name ?? 'Система'}</span>
          <span className="text-muted">{auditLabel[e.action] ?? e.action}</span>
          {e.target && e.target.id !== e.actor?.id && <span className="font-medium">{e.target.name}</span>}
          {subject(e) && <span className="font-medium">«{subject(e)}»</span>}
          {change(e) && <span className="text-muted">{change(e)}</span>}
          {showClient && e.clientName && <span className="text-xs text-faint">{e.clientName}</span>}
        </li>
      ))}
    </ul>
  );
}

function Pages({ filter, showClient }: { filter: AuditFilter; showClient: boolean }) {
  const query = useAuditLog(filter);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  if (!data) return <QueryPending query={query} />;
  const items = data.pages.flatMap((p) => p.items);
  if (items.length === 0) return <Empty title="Записей нет">Под этот отбор в журнале ничего не нашлось.</Empty>;
  return (
    <>
      <Rows items={items} showClient={showClient} />
      {hasNextPage && (
        <button className="btn-ghost mt-3" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Показать ещё
        </button>
      )}
    </>
  );
}

/** URL query name of each filter: the link on a client card (?clientId=…) opens the journal already narrowed. */
const QUERY: Record<keyof AuditFilter, string> = { actor: 'person', clientId: 'clientId', action: 'action', since: 'since', until: 'until' };

/** The whole journal (owners only): filters by person, client, action prefix and dates, and a CSV with the same filters. The filters live in the URL. */
export function AuditLog() {
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => {
    const f: AuditFilter = {};
    (Object.keys(QUERY) as (keyof AuditFilter)[]).forEach((k) => {
      const v = params.get(QUERY[k]);
      if (v) f[k] = v;
    });
    return f;
  }, [params]);
  const people = useStudioPeople().data ?? [];
  const clients = useStudioClients().data ?? [];
  const set = (k: keyof AuditFilter, v: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v) next.set(QUERY[k], v);
        else next.delete(QUERY[k]);
        return next;
      },
      { replace: true },
    );
  const prefixes = [...new Set(Object.keys(auditLabel).map((a) => a.split('_')[0]))];
  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3 text-sm">
        <label className="grid gap-1 text-xs text-muted">
          Кто
          <select className="field h-8 w-44 text-sm" aria-label="Кто" value={filter.actor ?? ''} onChange={(e) => set('actor', e.target.value)}>
            <option value="">Все</option>
            {people.map((p) => (
              <option key={p.person.id} value={p.person.id}>
                {p.person.name}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-xs text-muted">
          Клиент
          <select className="field h-8 w-44 text-sm" aria-label="Клиент в журнале" value={filter.clientId ?? ''} onChange={(e) => set('clientId', e.target.value)}>
            <option value="">Все</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-xs text-muted">
          Действие (начало названия)
          <input className="field h-8 w-44 text-sm" aria-label="Действие" list="audit-actions" placeholder="invoice, secret…" value={filter.action ?? ''} onChange={(e) => set('action', e.target.value.trim())} />
          <datalist id="audit-actions">
            {prefixes.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </label>
        <label className="grid gap-1 text-xs text-muted">
          С
          <input type="date" className="field h-8 text-sm" aria-label="С даты" value={filter.since ?? ''} onChange={(e) => set('since', e.target.value)} />
        </label>
        <label className="grid gap-1 text-xs text-muted">
          По
          <input type="date" className="field h-8 text-sm" aria-label="По дату" value={filter.until ?? ''} onChange={(e) => set('until', e.target.value)} />
        </label>
        <DownloadButton className="btn-secondary h-8 text-sm" label="Скачать CSV" name="audit.csv" load={() => studioApi.auditCsv(filter)}>
          <Download className="size-4" /> CSV
        </DownloadButton>
      </div>
      <Pages filter={filter} showClient />
    </>
  );
}

/** How many events the client card shows; the rest is in the journal. */
export const HISTORY_ON_CARD = 10;

/** «История» on the studio client card: the latest events done to this client. Owners only (the log is theirs). */
export function ClientHistory({ clientId }: { clientId: string }) {
  const q = useAuditHead({ clientId }, HISTORY_ON_CARD);
  const data = q.data;
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">История</h2>
      {!data ? (
        <QueryPending query={q} compact />
      ) : data.items.length === 0 ? (
        <Empty title="Записей нет">По этому клиенту в журнале пока ничего нет.</Empty>
      ) : (
        <Rows items={data.items.slice(0, HISTORY_ON_CARD)} showClient={false} />
      )}
      <Link className="btn-ghost mt-3 inline-flex" to={`/studio/admin/audit?clientId=${encodeURIComponent(clientId)}`}>
        Вся история клиента
      </Link>
    </section>
  );
}
